/**
 * Whether a guided tour has started in this browser tab's visit. One-time tips
 * hold back for the rest of the visit so onboarding never stacks two lessons.
 */
const STORAGE_KEY = "gail_tour_shown_this_visit";
const listeners = new Set<() => void>();
// Covers tabs where storage throws (privacy modes) for as long as the page lives.
let shownInMemory = false;

export function markTourShownThisVisit(): void {
  shownInMemory = true;
  try {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, "true");
  } catch {
    // Best-effort: the in-memory flag still holds for this page.
  }
  listeners.forEach((listener) => listener());
}

export function wasTourShownThisVisit(): boolean {
  if (shownInMemory) return true;
  try {
    return globalThis.sessionStorage?.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function subscribeTourVisit(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// One entry per tour showing right now, so overlapping or remounting tours release independently.
const toursOnScreen = new Set<symbol>();

/** Marks a tour as on screen until the returned release runs. */
export function trackTourOnScreen(): () => void {
  const tour = Symbol("tour");
  toursOnScreen.add(tour);
  listeners.forEach((listener) => listener());
  return () => {
    if (!toursOnScreen.delete(tour)) return;
    listeners.forEach((listener) => listener());
  };
}

export function isTourOnScreen(): boolean {
  return toursOnScreen.size > 0;
}
